import { useMemo } from 'react';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useApiQuery } from '@app/hooks/useApiQuery';
import KpiCard from '@app/components/KpiCard';
import CopyField from '@app/components/CopyField';
import { formatDateTime } from '@app/utils/format';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import type { OrganizationProfile } from '@app/types/org-console';

const OrgSettingsPage = () => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource } = useAuthFetch();

  const orgFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => fetchOrgResource<OrganizationProfile>(basePath, orgId);
  }, [basePath, orgId]);

  const {
    data: organization,
    loading,
    error,
    backendMissing
  } = useApiQuery<OrganizationProfile>(orgFetcher, [orgFetcher]);

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Organization Settings"
        description="Profile information and lightweight properties surfaced by the API."
        topSlot={<OrgModulePlaceholder intent="Select an organization to view settings." />}
      >
        <OrgModulePlaceholder intent="Organization profile form renders once an organization is active." />
      </OrgModuleLayout>
    );
  }

  if (backendMissing) {
    return (
      <OrgModuleLayout
        title="Organization Settings"
        description="Profile information and lightweight properties surfaced by the API."
        topSlot={<OrgModulePlaceholder intent="Implement this API in the backend to fetch organization details." />}
      >
        <OrgModulePlaceholder intent="Implement this API in the backend to save organization updates." />
      </OrgModuleLayout>
    );
  }

  const notes = organization?.notes ?? '';

  const topSlot = (
    <div className="settings-top">
      <div className="kpi-grid">
        <KpiCard label="Organization" value={organization?.name ?? '—'} />
        <KpiCard label="Created" value={formatDateTime(organization?.createdAt ?? null)} />
        <KpiCard label="Updated" value={formatDateTime(organization?.updatedAt ?? null)} />
      </div>
      {error && <OrgModulePlaceholder intent={`Failed to load organization profile: ${error.message}`} />}
    </div>
  );

  return (
    <OrgModuleLayout
      title="Organization Settings"
      description="Profile information and lightweight properties surfaced by the API."
      topSlot={topSlot}
    >
      {loading ? (
        <p>Loading organization profile…</p>
      ) : organization ? (
        <form className="org-settings-form">
          <label>
            <span>Name</span>
            <input type="text" value={organization.name ?? ''} readOnly />
          </label>
          <div className="form-field">
            <span className="form-field__label">Organization ID</span>
            <CopyField value={organization.id ?? ''} copyOnValueClick hideButton />
          </div>
          <label>
            <span>Notes</span>
            <textarea value={notes} readOnly />
          </label>
          <OrgModulePlaceholder intent="Form is read-only until PATCH endpoint is finalized. Add editable bindings once backend implements If-Match updates." />
        </form>
      ) : (
        <OrgModulePlaceholder intent="Organization profile not available." />
      )}
    </OrgModuleLayout>
  );
};

export default OrgSettingsPage;
